import React from 'react';
import { ContainerLayout, ContentLayout } from '@hyperflake/react-ui-library';
import { ProgressTabModal, ProgressTabProvider } from '@library/progress-tab';
import { AppNavbar } from 'modules/shared/features/navigation';
import AnalyticsSidebar from 'modules/shared/features/navigation/components/AnalyticsSidebar/AnalyticsSidebar';
import { Outlet } from 'react-router-dom';
import { CrossIcon } from '@icon/index';
import AppNavbarMobile from 'modules/shared/features/navigation/components/AppNavBarMobile/AppNavbarMobile';

/** Match Tailwind lg breakpoint (1024px) */
function useIsMobile(breakpoint = 1024) {
    const [isMobile, setIsMobile] = React.useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );

    React.useEffect(() => {
        const mq = window.matchMedia(`(max-width:${breakpoint - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [breakpoint]);

    return isMobile;
}

/** Desktop: unchanged container with AnalyticsSidebar */
function DesktopShell() {
    return (
        <ContainerLayout>
            <AppNavbar />
            <AnalyticsSidebar />
            <ContentLayout className="p-0">
                <Outlet />
            </ContentLayout>
        </ContainerLayout>
    );
}

/** Mobile drawer that wraps AnalyticsSidebar */
function MobileSidebarDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
    // close on Esc
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    // prevent body scroll when open
    React.useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    return (
        <div aria-hidden={!open} className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/40 transition-opacity ${open ? 'opacity-100' : 'opacity-0'}`}
            />
            {/* Panel */}
            <div
                id="mobile-sidebar"
                role="dialog"
                aria-label="Analytics sidebar"
                className={`absolute left-0 top-0 h-full w-72 max-w-[85vw] bg-white shadow-xl transition-transform ${
                    open ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex items-center justify-between border-b px-3 py-2">
                    <span className="font-semibold">Analytics</span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-50"
                    >
                        <CrossIcon size={18} />
                    </button>
                </div>
                <div className="h-[calc(100%-44px)] overflow-auto">
                    <AnalyticsSidebar />
                </div>
            </div>
        </div>
    );
}

/** Mobile shell: AppNavbarMobile + slide-in AnalyticsSidebar */
function MobileShell() {
    const [open, setOpen] = React.useState(false);

    return (
        <div className="flex min-h-screen flex-col">
            <AppNavbarMobile onMenuClick={() => setOpen((v) => !v)} isMenuOpen={open} setOpen={setOpen} />
            <MobileSidebarDrawer open={open} onClose={() => setOpen(false)} />
            <div className="flex-1 overflow-auto pt-[56px]">
                <Outlet />
            </div>
        </div>
    );
}

export default function AnalyticsLayout() {
    const isMobile = useIsMobile(1024); // Tailwind lg
    return (
        <ProgressTabProvider>
            {isMobile ? <MobileShell /> : <DesktopShell />}
            <ProgressTabModal />
        </ProgressTabProvider>
    );
}
